/* Nightdrop — shared motion engine. Vanilla JS, no dependencies.
   window.Nightdrop = { reveal, pin, parallax, tilt, onResize, section }
   One passive scroll listener + one rAF drive every pinned section;
   measurements are cached and refreshed on resize, never read per frame.

   Reduced-motion policy (merchant decision, see DESIGN-HANDOFF §quirks):
   scroll-scrubbed pins stay active — they only move when the visitor
   scrolls, which is direct manipulation, not autonomous motion. Reveals
   and idle/decorative animation are disabled via CSS and the flags below. */
(function () {
  'use strict';

  var reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer =
    window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function clamp01(v) {
    return v < 0 ? 0 : v > 1 ? 1 : v;
  }

  function easeOut(p) {
    var i = 1 - p;
    return 1 - i * i;
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* ----- resize bus -------------------------------------------------- */
  var resizeFns = [];
  var resizeTimer = null;

  function flushResize() {
    resizeTimer = null;
    for (var i = 0; i < resizeFns.length; i++) resizeFns[i]();
  }

  function queueResize() {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(flushResize, 150);
  }

  window.addEventListener('resize', queueResize);
  if (window.visualViewport) window.visualViewport.addEventListener('resize', queueResize);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(queueResize);

  function onResize(fn) {
    resizeFns.push(fn);
    return function () {
      var i = resizeFns.indexOf(fn);
      if (i > -1) resizeFns.splice(i, 1);
    };
  }

  /* ----- reveal ------------------------------------------------------ */
  var revealObserver = null;

  if (!reducedMotion && 'IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('ds-in');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
  }

  function reveal(el) {
    if (!el) return;
    if (revealObserver) {
      revealObserver.observe(el);
    } else {
      el.classList.add('ds-in');
    }
  }

  onReady(function () {
    document.querySelectorAll('.ds-reveal').forEach(reveal);

    /* PDP reveal targets (pdp-collection handoff §2): gallery cards, brand
       story, related cards, about banner. Collection grid entrances are
       pure CSS by design — facet re-renders must never depend on JS. */
    if (document.body.classList.contains('template-product')) {
      document
        .querySelectorAll(
          '.product__media-item, .rich-text__blocks, .related-products .card-wrapper, .banner__content'
        )
        .forEach(function (el) {
          el.classList.add('ds-reveal');
          reveal(el);
        });
    }
  });

  /* Editor rerenders replace section DOM with fresh .ds-reveal nodes that
     the initial pass never saw — without this they stay at opacity 0. */
  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('.ds-reveal').forEach(reveal);
  });

  /* ----- header chrome (menu-search handoff) -------------------------
   Close helpers route through Dawn's own summary toggles — its drawer
   and details-modal JS keep owning open/close, focus and ESC. */
  function closeViaSummary(details) {
    if (details && details.hasAttribute('open')) {
      var summary = details.querySelector('summary');
      /* non-bubbling: a bubbled click reaches Dawn's details-modal
         body-click handler and closes the OTHER panel too */
      if (summary) summary.dispatchEvent(new MouseEvent('click', { bubbles: false, cancelable: true }));
    }
  }

  document.addEventListener('click', function (event) {
    var closeBtn = event.target.closest('.nd-drawer-close');
    var backdrop = event.target.closest('.nd-drawer-backdrop');
    if (closeBtn || backdrop) {
      closeViaSummary((closeBtn || backdrop).closest('details'));
    }
  });

  /* Menu and search are mutually exclusive: opening one closes the other */
  onReady(function () {
    var drawerDetails = document.querySelector('header-drawer details');
    var searchDetails = document.querySelector('details-modal.header__search details');
    if (!drawerDetails || !searchDetails) return;
    drawerDetails.addEventListener('toggle', function () {
      if (drawerDetails.hasAttribute('open')) closeViaSummary(searchDetails);
    });
    searchDetails.addEventListener('toggle', function () {
      if (searchDetails.hasAttribute('open')) closeViaSummary(drawerDetails);
    });
  });

  /* ----- pin / scrub -------------------------------------------------
   A pin is a section taller than the viewport with a sticky child; the
   scrub progress is p = (scrollY - top) / (height - viewportHeight).
   Layout reads happen only in measure(); the scroll handler just stores
   scrollY and schedules one shared rAF. */
  var pins = [];
  var rafId = null;
  var lastScrollY = window.pageYOffset;

  var pinObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              for (var i = 0; i < pins.length; i++) {
                if (pins[i].section === entry.target) pins[i].visible = entry.isIntersecting;
              }
            });
            schedule();
          },
          { rootMargin: '20% 0px 20% 0px' }
        )
      : null;

  function measurePin(entry) {
    var rect = entry.section.getBoundingClientRect();
    entry.top = rect.top + window.pageYOffset;
    entry.range = Math.max(1, entry.section.offsetHeight - window.innerHeight);
  }

  function frame() {
    rafId = null;
    for (var i = 0; i < pins.length; i++) {
      var entry = pins[i];
      if (!entry.visible && entry.settled) continue;
      var p = clamp01((lastScrollY - entry.top) / entry.range);
      entry.settled = p === 0 || p === 1;
      if (p === entry.last) continue;
      entry.last = p;
      entry.cb(p, easeOut(p));
    }
  }

  function schedule() {
    if (rafId === null) rafId = window.requestAnimationFrame(frame);
  }

  function onScroll() {
    lastScrollY = window.pageYOffset;
    schedule();
  }

  var scrollBound = false;

  function pin(section, cb) {
    if (!section || typeof cb !== 'function') return { destroy: function () {}, refresh: function () {} };
    var entry = { section: section, cb: cb, top: 0, range: 1, last: -1, visible: true, settled: false };
    measurePin(entry);
    pins.push(entry);
    if (pinObserver) pinObserver.observe(section);
    if (!scrollBound) {
      window.addEventListener('scroll', onScroll, { passive: true });
      scrollBound = true;
    }
    var offResize = onResize(function () {
      measurePin(entry);
      entry.last = -1;
      schedule();
    });
    schedule();
    return {
      refresh: function () {
        measurePin(entry);
        entry.last = -1;
        schedule();
      },
      destroy: function () {
        var i = pins.indexOf(entry);
        if (i > -1) pins.splice(i, 1);
        if (pinObserver) pinObserver.unobserve(section);
        offResize();
      }
    };
  }

  /* ----- parallax ----------------------------------------------------
   Cursor parallax over a container: children carry data-depth. Target
   offsets follow the pointer; rendering lerps toward the target so the
   motion has ease-back weight instead of sticking to the cursor. */
  function parallax(container, opts) {
    /* Pointer-driven; runs under reduced motion per the merchant's recorded
       override (see DESIGN-HANDOFF §10) — it only moves with the cursor. */
    if (!container || !finePointer) {
      return { destroy: function () {} };
    }
    opts = opts || {};
    var strengthX = opts.strengthX || 48;
    var strengthY = opts.strengthY || 36;
    var items = [];
    container.querySelectorAll('[data-depth]').forEach(function (el) {
      items.push({ el: el, depth: parseFloat(el.getAttribute('data-depth')) || 1, x: 0, y: 0 });
    });
    if (!items.length) return { destroy: function () {} };

    var targetX = 0;
    var targetY = 0;
    var running = false;
    var parallaxRaf = null;

    function tick() {
      var alive = false;
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var gx = -targetX * strengthX * it.depth;
        var gy = -targetY * strengthY * it.depth;
        it.x += (gx - it.x) * 0.08;
        it.y += (gy - it.y) * 0.08;
        if (Math.abs(gx - it.x) > 0.1 || Math.abs(gy - it.y) > 0.1) alive = true;
        it.el.style.transform = 'translate3d(' + it.x.toFixed(2) + 'px,' + it.y.toFixed(2) + 'px,0)';
      }
      if (alive) {
        parallaxRaf = window.requestAnimationFrame(tick);
      } else {
        running = false;
        parallaxRaf = null;
      }
    }

    function start() {
      if (!running) {
        running = true;
        parallaxRaf = window.requestAnimationFrame(tick);
      }
    }

    function onMove(event) {
      var rect = container.getBoundingClientRect();
      targetX = (event.clientX - rect.left) / rect.width - 0.5;
      targetY = (event.clientY - rect.top) / rect.height - 0.5;
      start();
    }

    function onLeave() {
      targetX = 0;
      targetY = 0;
      start();
    }

    container.addEventListener('pointermove', onMove);
    container.addEventListener('pointerleave', onLeave);

    return {
      destroy: function () {
        container.removeEventListener('pointermove', onMove);
        container.removeEventListener('pointerleave', onLeave);
        if (parallaxRaf) window.cancelAnimationFrame(parallaxRaf);
      }
    };
  }

  /* ----- tilt -------------------------------------------------------- */
  function tilt(el, opts) {
    /* Pointer-driven; merchant override keeps it active under reduced motion */
    if (!el || !finePointer) return { destroy: function () {} };
    opts = opts || {};
    var max = opts.max || 6;
    var perspective = opts.perspective || 800;

    function onMove(event) {
      var rect = el.getBoundingClientRect();
      var x = (event.clientX - rect.left) / rect.width - 0.5;
      var y = (event.clientY - rect.top) / rect.height - 0.5;
      el.style.transform =
        'perspective(' + perspective + 'px) rotateX(' + (-y * max).toFixed(2) + 'deg) rotateY(' + (x * max).toFixed(2) + 'deg)';
    }

    function onLeave() {
      el.style.transform = '';
    }

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);

    return {
      destroy: function () {
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerleave', onLeave);
        el.style.transform = '';
      }
    };
  }

  /* ----- section lifecycle -------------------------------------------
   init(root) runs per section instance and may return a cleanup
   function; the theme editor's load/unload events re-run it. A type
   registers once per page — editor re-renders re-execute the section's
   inline script, and a second registration would double-bind the
   document listeners below. */
  var sectionTypes = {};

  function section(type, init) {
    if (sectionTypes[type]) return;
    sectionTypes[type] = true;
    var cleanups = new Map();
    var selector = '[data-nd-section="' + type + '"]';

    function mount(root) {
      if (cleanups.has(root)) return;
      var cleanup = init(root);
      cleanups.set(root, typeof cleanup === 'function' ? cleanup : function () {});
    }

    onReady(function () {
      document.querySelectorAll(selector).forEach(mount);
    });

    document.addEventListener('shopify:section:load', function (event) {
      var root = event.target.matches && event.target.matches(selector) ? event.target : event.target.querySelector(selector);
      if (root) mount(root);
      queueResize();
    });

    document.addEventListener('shopify:section:unload', function (event) {
      cleanups.forEach(function (cleanup, root) {
        if (event.target === root || event.target.contains(root)) {
          cleanup();
          cleanups.delete(root);
        }
      });
    });
  }

  /* ----- tips: hover / focus "spec line" for [data-nd-tip] ----------------
     One mono line drawn in from a 1px accent rule. Singleton on <body>,
     position: fixed, document-level delegation (nothing to re-init when
     sections or the drawer re-render). Mouse hover (120ms intent delay,
     instant when hopping to a neighbouring pill) and keyboard focus only;
     touch keeps the plain label. */
  var tipEl = null;
  var tipFor = null;
  var tipTimer = 0;
  var tipLastHide = 0;
  var TIP_DELAY = 120;
  var TIP_HOP = 400;

  function tipNode() {
    if (tipEl) return tipEl;
    tipEl = document.createElement('div');
    tipEl.className = 'nd-tip';
    tipEl.id = 'nd-tip';
    tipEl.setAttribute('role', 'tooltip');
    var rule = document.createElement('span');
    rule.className = 'nd-tip__rule';
    var line = document.createElement('span');
    line.className = 'nd-tip__line';
    tipEl.appendChild(rule);
    tipEl.appendChild(line);
    document.body.appendChild(tipEl);
    return tipEl;
  }

  function tipPlace(el, trigger) {
    el.classList.remove('is-below');
    el.style.left = '0px';
    el.style.top = '0px';
    var t = el.getBoundingClientRect();
    /* A [data-nd-tip-slot] ancestor (the drawer footer) reserves a band at
       its top: every pill inside shares that one slot, so the line never
       lands on the scrolling menu list above. */
    var slot = trigger.closest ? trigger.closest('[data-nd-tip-slot]') : null;
    if (slot) {
      var sr = slot.getBoundingClientRect();
      var sx = Math.round(Math.min(Math.max(8, sr.left + sr.width / 2 - t.width / 2), window.innerWidth - t.width - 8));
      el.style.left = sx + 'px';
      el.style.top = Math.round(sr.top + (parseFloat(slot.getAttribute('data-nd-tip-slot')) || 12)) + 'px';
      return;
    }
    var r = trigger.getBoundingClientRect();
    var gap = 10;
    var x = Math.round(Math.min(Math.max(8, r.left + r.width / 2 - t.width / 2), window.innerWidth - t.width - 8));
    var above = r.top - t.height - gap;
    var below = above < 8;
    if (below) el.classList.add('is-below');
    el.style.left = x + 'px';
    el.style.top = Math.round(below ? r.bottom + gap : above) + 'px';
  }

  function tipShow(trigger, instant) {
    var text = trigger.getAttribute('data-nd-tip');
    if (!text) return;
    var el = tipNode();
    var same = tipFor === trigger && el.classList.contains('is-on');
    el.lastChild.textContent = text.replace(/\s*\n+\s*/g, ' · ').trim();
    tipPlace(el, trigger);
    if (tipFor && tipFor !== trigger) tipFor.removeAttribute('aria-describedby');
    tipFor = trigger;
    trigger.setAttribute('aria-describedby', 'nd-tip');
    if (same) return;
    el.classList.remove('is-on');
    el.classList.toggle('is-instant', !!instant);
    requestAnimationFrame(function () {
      if (tipFor === trigger) el.classList.add('is-on');
    });
  }

  function tipHide() {
    clearTimeout(tipTimer);
    if (tipEl && tipEl.classList.contains('is-on')) tipLastHide = Date.now();
    if (tipEl) tipEl.classList.remove('is-on', 'is-instant');
    if (tipFor) tipFor.removeAttribute('aria-describedby');
    tipFor = null;
  }

  function tipTrigger(node) {
    return node && node.closest ? node.closest('[data-nd-tip]') : null;
  }

  document.addEventListener('pointerover', function (e) {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    var t = tipTrigger(e.target);
    if (!t || t === tipFor) return;
    clearTimeout(tipTimer);
    var hop = tipEl && (tipEl.classList.contains('is-on') || Date.now() - tipLastHide < TIP_HOP);
    if (hop) { tipShow(t, true); return; }
    tipTimer = setTimeout(function () {
      var still = false;
      try { still = t.matches(':hover'); } catch (err) { still = true; }
      if (still) tipShow(t, false);
    }, TIP_DELAY);
  });
  document.addEventListener('pointerout', function (e) {
    var t = tipTrigger(e.target);
    if (!t || t.contains(e.relatedTarget)) return;
    clearTimeout(tipTimer);
    if (t === tipFor) tipHide();
  });
  document.addEventListener('focusin', function (e) {
    var t = tipTrigger(e.target);
    if (!t) return;
    var visible = true;
    try { visible = t.matches(':focus-visible'); } catch (err) { visible = true; }
    if (visible) tipShow(t, true);
  });
  document.addEventListener('focusout', function (e) {
    if (tipTrigger(e.target) === tipFor) tipHide();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') tipHide();
  });
  document.addEventListener('pointerdown', tipHide, true);
  /* Scrolling moves the trigger; keep the line attached while the pill is
     still hovered or focused, drop it otherwise. */
  function tipFollow() {
    if (!tipFor) return;
    var keep = false;
    try { keep = tipFor.matches(':hover') || tipFor === document.activeElement; } catch (err) { keep = false; }
    if (keep) tipPlace(tipEl, tipFor); else tipHide();
  }
  document.addEventListener('scroll', tipFollow, { capture: true, passive: true });
  document.addEventListener('shopify:section:unload', tipHide);
  document.addEventListener('visibilitychange', tipHide);
  onResize(tipFollow);

  /* ----- scroll lock companion --------------------------------------
     Dawn marks the page with body.overflow-hidden(-mobile|-tablet|-desktop)
     while the menu drawer, filter drawer, cart drawer, search overlay or a
     modal is open, and base.css turns that into overflow:hidden on the
     viewport. Desktop is done at that point. Phones ignore it for touch
     scrolling, so while a lock is on we run the body-scroll-lock
     algorithm: a touchmove is allowed only if some scroll container
     between the touch and the open panel can still move in that
     direction; everything else is cancelled (no chaining when the list
     fits, no rubber-band at its edges). Listeners exist only while
     locked, and the scroll position is restored on unlock in case Safari
     scrolled a focused field into view. */
  var ND_LOCK = '.overflow-hidden, .overflow-hidden-mobile, .overflow-hidden-tablet, .overflow-hidden-desktop';
  var ND_PANELS = '.menu-drawer, .mobile-facets__inner, cart-drawer .drawer__inner, .cart-drawer .drawer__inner, .search-modal, .modal__content, .quick-add-modal__content, pickup-availability-drawer';
  var ndLocked = false;
  var ndLastY = null;
  var ndSavedY = 0;
  var ndSbw = 0; /* classic-scrollbar width, sampled while unlocked */

  function ndSampleScrollbar() {
    if (ndLocked) return;
    ndSbw = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
  }

  function ndCanAbsorb(el, dy) {
    var oy = getComputedStyle(el).overflowY;
    if (oy !== 'auto' && oy !== 'scroll') return false;
    if (el.scrollHeight <= el.clientHeight) return false;
    if (dy > 0) return el.scrollTop > 0;
    if (dy < 0) return el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    return false;
  }

  function ndTouchStart(e) {
    ndLastY = e.touches.length === 1 ? e.touches[0].clientY : null;
  }

  function ndTouchMove(e) {
    if (e.touches.length !== 1) return; /* pinch-zoom stays native */
    var y = e.touches[0].clientY;
    var dy = ndLastY === null ? 0 : y - ndLastY;
    ndLastY = y;
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.matches && t.matches('input[type="range"]')) return;
    var panel = t.closest(ND_PANELS);
    var allow = false;
    if (panel) {
      for (var el = t; el; el = el.parentElement) {
        if (el.nodeType === 1 && ndCanAbsorb(el, dy)) { allow = true; break; }
        if (el === panel) break;
      }
    }
    if (!allow && e.cancelable) e.preventDefault();
  }

  function ndSetLock(on) {
    if (on === ndLocked) return;
    ndLocked = on;
    if (on) {
      ndSavedY = window.pageYOffset;
      ndLastY = null;
      /* the viewport's scrollbar disappears with the lock (scrollbar-gutter
         is ignored when overflow arrives via body propagation); pad body
         by the same width so nothing shifts, like Bootstrap's ScrollBarHelper */
      if (ndSbw > 0) document.body.style.paddingRight = ndSbw + 'px';
      document.addEventListener('touchstart', ndTouchStart, { passive: true });
      document.addEventListener('touchmove', ndTouchMove, { passive: false });
    } else {
      document.removeEventListener('touchstart', ndTouchStart);
      document.removeEventListener('touchmove', ndTouchMove);
      document.body.style.paddingRight = '';
      if (Math.abs(window.pageYOffset - ndSavedY) > 1) {
        try { window.scrollTo({ top: ndSavedY, behavior: 'instant' }); } catch (err) { window.scrollTo(0, ndSavedY); }
      }
    }
  }

  if (window.MutationObserver && document.body) {
    ndSampleScrollbar();
    onResize(ndSampleScrollbar);
    new MutationObserver(function () {
      ndSetLock(document.body.matches(ND_LOCK));
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    ndSetLock(document.body.matches(ND_LOCK));
  }

  window.Nightdrop = {
    reducedMotion: reducedMotion,
    finePointer: finePointer,
    clamp01: clamp01,
    easeOut: easeOut,
    reveal: reveal,
    pin: pin,
    parallax: parallax,
    tilt: tilt,
    onResize: onResize,
    section: section,
    tips: { show: tipShow, hide: tipHide }
  };

  /* Archive-era sections call ArchiveReveal.observe on rerender. */
  window.ArchiveReveal = { observe: reveal };

  /* Sections normally register on DOMContentLoaded, but on slow or flaky
     connections this script can arrive after that event (or on a retry) —
     announce readiness so late registrations still boot. */
  try {
    document.dispatchEvent(new CustomEvent('nightdrop:ready'));
  } catch (e) {
    /* CustomEvent unavailable: nothing to announce to */
  }
})();
